import { strippedHrefCharacters } from './policy';

/**
 * Character-level rules the validator and the sanitizer share, so both apply one definition of
 * each. They exist because a rule that reads correctly in JavaScript can still disagree with an
 * HTML or CSS parser about the same bytes.
 */

/**
 * The five characters an HTML tokenizer treats as whitespace. JavaScript's `\s` matches twenty
 * more that a tokenizer reads as ordinary characters, so nothing here may use `\s` or
 * `String.prototype.trim` on markup — markup fits in the gap between the two definitions.
 */
export const HTML_WHITESPACE = '\t\n\f\r ';

/** HTML whitespace as a character class, for the places that need a pattern. */
export const HTML_WHITESPACE_PATTERN = /[\t\n\f\r ]/;

const HTML_WHITESPACE_EDGES = /^[\t\n\f\r ]+|[\t\n\f\r ]+$/g;

/**
 * Numeric references, terminated or not. A parser decodes these AFTER delimiting the attribute, so
 * a validator reading the raw value sees `&#59` where the parser will see `;`.
 */
const NUMERIC_REFERENCE_PATTERN = /&#(x[\da-f]+|\d+);?/gi;

/** Any named character reference, terminated or not. */
const NAMED_REFERENCE_PATTERN = /&([a-z][a-z\d]*);?/gi;

/**
 * Numeric references and `&quot;`, matched together so one pass decodes both. Three rules, each
 * matching what a parser does and each load-bearing:
 *
 * - the named half is case-SENSITIVE, because the HTML table holds only `quot` and `QUOT`; a
 *   parser leaves `&QuOt;` as text, so decoding it would read a string the parser never builds;
 * - the numeric half is case-INSENSITIVE in both its marker and its hex digits;
 * - the semicolon-less form decodes only when what follows cannot continue a name, so `&quotx`
 *   stays text inside an attribute while `&quot,` decodes.
 */
const STYLE_REFERENCE_PATTERN = /&(?:#([xX][\dA-Fa-f]+|\d+);?|(?:quot|QUOT)(?:;|(?![\dA-Za-z=])))/g;

/** True when the character is one of the five the HTML tokenizer treats as whitespace. */
export function isHtmlWhitespace(character: string): boolean {
  return character !== '' && HTML_WHITESPACE.includes(character);
}

/** `String.prototype.trim` restricted to the five characters HTML calls whitespace. */
export function trimHtmlWhitespace(value: string): string {
  return value.replace(HTML_WHITESPACE_EDGES, '');
}

function fromCodePoint(digits: string, fallback: string): string {
  const codePoint = digits[0].toLowerCase() === 'x' ? parseInt(digits.slice(1), 16) : parseInt(digits, 10);

  if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return fallback;

  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return fallback;
  }
}

/**
 * Decode numeric references so a value is measured against the grammar a parser will apply to it.
 * Named ones are left alone — resolving them needs the whole entity table — and callers refuse the
 * ones that could matter instead.
 */
export function decodeNumericCharacterReferences(value: string): string {
  return value.replace(NUMERIC_REFERENCE_PATTERN, (match, digits: string) => fromCodePoint(digits, match));
}

/**
 * The numeric references plus `&quot;` — the one named reference a style attribute legitimately
 * carries, because a font family with a space in its name is quoted and the DOM escapes those
 * quotes on serialization.
 *
 * ONE pass, never two chained: decoding numerically first would turn `&#38;quot;` into `&quot;`
 * and a second pass would make it `"`, a decoding no browser performs.
 */
export function decodeStyleCharacterReferences(value: string): string {
  return value.replace(STYLE_REFERENCE_PATTERN, (match: string, digits: string | undefined) =>
    digits === undefined ? '"' : fromCodePoint(digits, match),
  );
}

function isCssNewline(character: string): boolean {
  return character === '\n' || character === '\r' || character === '\f';
}

/**
 * What a preserved value may carry outside a quoted string: what lengths, families, weights and the
 * `font` shorthand need. Three omissions carry the weight — `()` keeps `url(` out, `\` keeps an
 * escape from appearing outside a string, `&` keeps a character reference from coming back out of
 * the DOM. Colours have a functional syntax and are matched by the colour pattern, never here.
 */
const SAFE_CSS_VALUE_CHARACTER = /[A-Za-z0-9 \t.,%/#+\-_]/;

/**
 * Refused ANYWHERE in a preserved value, quoted or escaped included: a string protects a character
 * from the CSS tokenizer, not from the validator's whole-declaration checks, which refuse `&` as a
 * character reference and a comment by substring. `*` rather than the pair, since `/` alone is
 * needed by the `font` shorthand.
 */
const UNSAFE_CSS_VALUE_CHARACTERS = ['&', '*'];

/**
 * Quoted strings that actually close, and otherwise only the characters above. The sanitizer drops
 * what this rejects and the validator refuses it, so the two cannot disagree.
 */
export function isSafeCssValue(value: string): boolean {
  if (UNSAFE_CSS_VALUE_CHARACTERS.some(character => value.includes(character))) return false;

  let quote: string | null = null;
  let index = 0;

  while (index < value.length) {
    const character = value[index];
    const next = value[index + 1];

    if (quote !== null) {
      if (character === '\\' && next !== undefined) {
        index += 2;
        continue;
      }

      // An unescaped newline leaves the string unclosed, free to swallow what follows.
      if (isCssNewline(character)) return false;

      if (character === quote) quote = null;

      index += 1;
      continue;
    }

    if (character === '"' || character === "'") {
      quote = character;
      index += 1;
      continue;
    }

    if (!SAFE_CSS_VALUE_CHARACTER.test(character)) return false;

    index += 1;
  }

  return quote === null;
}

/**
 * Split on the semicolons that actually separate declarations, per CSS Syntax Level 3 string
 * tokens. One scan answers both questions callers have, so the answers cannot disagree: a `;`
 * inside a string separates nothing, and a string that never closes swallows everything after it.
 *
 * Counting quotes would not do: `font-family: "John's Font"` is valid and holds an odd number.
 */
export function scanCssDeclarations(value: string): { declarations: string[]; unterminated: boolean } {
  const declarations: string[] = [];

  let current = '';
  let quote: string | null = null;
  let unterminated = false;
  let index = 0;

  while (index < value.length) {
    const character = value[index];
    const next = value[index + 1];

    // A backslash escapes the next character in BOTH states: outside a string it stops `\"` from
    // opening one, inside it stops `\"` from closing one. The exception is a newline, which is a
    // continuation inside a string and not an escape outside one.
    if (next !== undefined && character === '\\' && (quote !== null || !isCssNewline(next))) {
      current += character + next;
      index += 2;
      continue;
    }

    if (quote === null) {
      if (character === '"' || character === "'") quote = character;

      if (character === ';') {
        declarations.push(current);
        current = '';
      } else {
        current += character;
      }

      index += 1;
      continue;
    }

    if (isCssNewline(character)) {
      unterminated = true;
      quote = null;
    } else if (character === quote) {
      quote = null;
    }

    current += character;
    index += 1;
  }

  declarations.push(current);

  return { declarations, unterminated: unterminated || quote !== null };
}

/**
 * The first named reference not on the inert list, or `null`. `&amp;` is inert by default: a query
 * string round-trips to it, and it decodes to a character that introduces nothing.
 */
export function findForbiddenCharacterReference(value: string, inert: readonly string[] = ['amp']): string | null {
  NAMED_REFERENCE_PATTERN.lastIndex = 0;

  let match = NAMED_REFERENCE_PATTERN.exec(value);

  while (match) {
    if (!inert.includes(match[1].toLowerCase())) return match[0];

    match = NAMED_REFERENCE_PATTERN.exec(value);
  }

  return null;
}

/**
 * An `href` as a URL parser will see it. Every `href` check runs on this form, never on the raw
 * attribute, or `/<TAB>/host` passes the root-relative rule and still resolves off-origin.
 */
export function normalizeHref(href: string): string {
  return trimHtmlWhitespace(href.trim()).replace(strippedHrefCharacters, '');
}
